// Mirrors afto_api's lib/constants.js FEATURE_KEYS — keep both in sync.
// Website, Loyalty, and Marketing Automation are each their own paid plan —
// a business can subscribe to any combination independently.
export const FEATURE_KEYS = {
  WEBSITE: 'website',
  LOYALTY: 'loyalty',
  MARKETING_AUTOMATION: 'marketing_automation',
};

export const ALL_FEATURE_KEYS = Object.values(FEATURE_KEYS);

export const FEATURE_LABELS = {
  [FEATURE_KEYS.WEBSITE]: 'Website',
  [FEATURE_KEYS.LOYALTY]: 'Loyalty',
  [FEATURE_KEYS.MARKETING_AUTOMATION]: 'Marketing Automation',
};

// Mirrors afto_api's lib/constants.js WEBSITE_FREE_TIER_DAYS. Website only —
// Loyalty and Marketing Automation have no free tier at all.
export const WEBSITE_FREE_TIER_DAYS = 14;

// Mirrors afto_api's lib/constants.js WEBSITE_TIERS.FREE.
export const WEBSITE_FREE_TIER = 'free';

// Mirrors afto_api's lib/constants.js BILLING_MODELS. A revenue-share plan is
// paid as commission deducted from the weekly settlement payout instead of a
// recurring Stripe charge, so it carries no price, renewal date or Stripe
// subscription id.
export const BILLING_MODELS = {
  SUBSCRIPTION: 'subscription',
  REVENUE_SHARE: 'revenue_share',
};

// Mirrors the API's PAYMENT_FAILED_SUBSCRIPTION_STATUSES: a charge failed and
// Stripe is still retrying. The plan is not gone, so it must not be offered
// for sale again.
export const PAYMENT_FAILED_SUBSCRIPTION_STATUSES = ['past_due', 'unpaid'];

export const TIER_LABELS = {
  free: 'Free Pack',
  starter: 'Starter Pack',
  medium: 'Medium Pack',
  custom: 'Custom Pack',
};

// Only these tiers are purchasable through Stripe checkout/tier-switch —
// Free (Website only) has no Stripe object and Custom is contact-only for
// all three. Mirrors afto_api's stripeValidator.js PURCHASABLE_TIERS.
export const PURCHASABLE_TIERS = ['starter', 'medium'];

// Mirrors afto_api's lib/constants.js *_TIER_PRICES_USD (dollars here, not
// cents) — shown only while the live Stripe catalog is still loading.
export const FEATURE_TIER_FALLBACK_PRICE = {
  [FEATURE_KEYS.WEBSITE]: { starter: 49.99, medium: 129.99 },
  [FEATURE_KEYS.LOYALTY]: { starter: 99, medium: 129 },
  // The source pricing table has no dedicated base-price row for Marketing
  // Automation (unlike Website's explicit "Pricing" row) — reuses Website's
  // numbers pending a real confirmed figure.
  [FEATURE_KEYS.MARKETING_AUTOMATION]: { starter: 49.99, medium: 129.99 },
};

// Mirrors afto_api's lib/constants.js ADDON_KEYS — keep both in sync. Each
// add-on belongs to exactly one feature (see ADDON_FEATURE_KEY) and is only
// purchasable by a business already subscribed to that feature — never a
// cross-feature marketplace.
export const ADDON_KEYS = {
  MOBILE_APP: 'mobile_app',
  STORE_APP_TABLET: 'store_app_tablet',
  WHATSAPP_ORDERING: 'whatsapp_ordering',
  VOICE_AI_ORDERING: 'voice_ai_ordering',
  PHONE_SUPPORT: 'phone_support',
  SEO_AEO: 'seo_aeo',
  SOCIAL_MEDIA_POSTS: 'social_media_posts',
  PAID_ADS: 'paid_ads',
};

// Object key order drives each feature's checkbox list display order.
export const ALL_ADDON_KEYS = Object.values(ADDON_KEYS);

// Which feature plan each add-on belongs to. Loyalty has none — every other
// row in its source pricing table is a plain included capability, no "+$X"
// line items.
export const ADDON_FEATURE_KEY = {
  [ADDON_KEYS.MOBILE_APP]: FEATURE_KEYS.WEBSITE,
  [ADDON_KEYS.STORE_APP_TABLET]: FEATURE_KEYS.WEBSITE,
  [ADDON_KEYS.WHATSAPP_ORDERING]: FEATURE_KEYS.WEBSITE,
  [ADDON_KEYS.VOICE_AI_ORDERING]: FEATURE_KEYS.WEBSITE,
  [ADDON_KEYS.PHONE_SUPPORT]: FEATURE_KEYS.WEBSITE,
  [ADDON_KEYS.SEO_AEO]: FEATURE_KEYS.MARKETING_AUTOMATION,
  [ADDON_KEYS.SOCIAL_MEDIA_POSTS]: FEATURE_KEYS.MARKETING_AUTOMATION,
  [ADDON_KEYS.PAID_ADS]: FEATURE_KEYS.MARKETING_AUTOMATION,
};

// Derived from ADDON_FEATURE_KEY rather than listed separately, so a feature
// can never be offered an add-on step it has no add-ons for. Loyalty has none,
// so its checkout goes straight to Stripe.
export const hasAddonsForFeature = (featureKey) =>
  Object.values(ADDON_FEATURE_KEY).includes(featureKey);

export const ADDON_LABELS = {
  [ADDON_KEYS.MOBILE_APP]: 'Mobile Shopper App',
  [ADDON_KEYS.STORE_APP_TABLET]: 'Store App + Tablet',
  [ADDON_KEYS.WHATSAPP_ORDERING]: 'WhatsApp AI Agent ordering (usage charges extra)',
  [ADDON_KEYS.VOICE_AI_ORDERING]: 'Voice AI Agent ordering (usage/min charges extra)',
  [ADDON_KEYS.PHONE_SUPPORT]: 'Phone Support (12-hour SLA)',
  [ADDON_KEYS.SEO_AEO]: 'SEO + AEO services',
  [ADDON_KEYS.SOCIAL_MEDIA_POSTS]: 'Social Media Posts + calendar management',
  [ADDON_KEYS.PAID_ADS]: 'Paid Ads on Meta/Google/TikTok (+ platform ad-spend charges)',
};

// Comparison-table sections that are sold wholly as an add-on, so none of
// their rows count as "included in the plan". The source sheet prices only the
// first row of each (Facebook: "2 posts/week (addn $49)") and leaves the rest
// bare ("Instagram: 2 posts/week"), which on its own reads as free. They are
// the Social Media Posts and Paid Ads add-ons (see ADDON_KEYS).
export const ADDON_ONLY_COMPARISON_SECTIONS = {
  [FEATURE_KEYS.MARKETING_AUTOMATION]: ['Social Media Posts', 'Paid Ads'],
};

// One short marketing line per feature x tier, shown on the pricing cards.
export const FEATURE_TIER_DESCRIPTIONS = {
  [FEATURE_KEYS.WEBSITE]: {
    free: 'Try a full online store for 14 days, no card required',
    starter: 'For businesses ready to sell online',
    medium: 'For growing stores that need integrations',
    custom: 'For large catalogs with custom needs',
  },
  [FEATURE_KEYS.LOYALTY]: {
    starter: 'For businesses launching their first rewards program',
    medium: 'For businesses scaling repeat purchases',
    custom: 'For loyalty programs at scale',
  },
  [FEATURE_KEYS.MARKETING_AUTOMATION]: {
    starter: 'For businesses starting with automated marketing',
    medium: 'For businesses running campaigns across channels',
    custom: 'For high-volume marketing operations',
  },
};

// Mirrors afto_api's lib/constants.js ADDON_TIER_PRICES_USD (dollars here,
// not cents) — shown only while the live Stripe catalog is still loading.
export const ADDON_TIER_FALLBACK_PRICE = {
  starter: {
    [ADDON_KEYS.MOBILE_APP]: 49,
    [ADDON_KEYS.STORE_APP_TABLET]: 49,
    [ADDON_KEYS.WHATSAPP_ORDERING]: 49,
    [ADDON_KEYS.VOICE_AI_ORDERING]: 49,
    [ADDON_KEYS.PHONE_SUPPORT]: 49,
    [ADDON_KEYS.SEO_AEO]: 49,
    [ADDON_KEYS.SOCIAL_MEDIA_POSTS]: 49,
    [ADDON_KEYS.PAID_ADS]: 49,
  },
  medium: {
    [ADDON_KEYS.MOBILE_APP]: 99,
    [ADDON_KEYS.STORE_APP_TABLET]: 99,
    [ADDON_KEYS.WHATSAPP_ORDERING]: 99,
    [ADDON_KEYS.VOICE_AI_ORDERING]: 99,
    [ADDON_KEYS.PHONE_SUPPORT]: 99,
    [ADDON_KEYS.SEO_AEO]: 99,
    [ADDON_KEYS.SOCIAL_MEDIA_POSTS]: 99,
    [ADDON_KEYS.PAID_ADS]: 99,
  },
};

// The actual row-by-row feature comparison content, transcribed directly
// from the source pricing sheets — one table per feature, grouped into the
// same visual sections the sheets use. "+$X" cells describe real add-ons
// (see ADDON_KEYS) rather than being purely informational text; a handful
// of rows (POS integration, onboarding support, the 5 automation triggers,
// etc.) are included at that tier with no extra charge and have no
// corresponding add-on.
export const FEATURE_COMPARISON_TABLES = {
  [FEATURE_KEYS.WEBSITE]: [
    {
      section: 'Core Offering',
      rows: [
        { label: 'Pricing', free: 'FREE 14-Day validity', starter: '$49.99', medium: '$129.99', custom: 'Custom pricing' },
        { label: 'Product limit', free: '100 Products', starter: '1,000 products', medium: '5,000 products', custom: 'Unlimited products' },
        { label: 'Shipping (local/International)', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Local Delivery (Uber/DoorDash) — 1 hour delivery', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Website Customization', free: 'No', starter: 'Yes, 3-Hour package', medium: 'Yes, 5-Hour package', custom: 'Yes, full customization' },
        { label: 'Hosting', free: 'Yes — 14 days', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Mobile Shopper App', free: 'No', starter: 'Yes, with additional $49', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'Store App + Tablet', free: 'No', starter: 'Yes, with additional $49', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'AI Shopping Assistant — on web + mobile', free: 'Yes — 14 days', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'AI Store Assistant', free: 'Yes — 14 days', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
      ],
    },
    {
      section: 'Integrations',
      rows: [
        { label: 'Inventory Integration with POS or other systems', free: 'No', starter: 'No', medium: 'Yes (1 system)', custom: 'Yes' },
        { label: 'CRM integration', free: 'No', starter: 'No', medium: 'Yes (1 system)', custom: 'Yes' },
      ],
    },
    {
      section: 'AI Channels',
      rows: [
        { label: 'WhatsApp AI Agent ordering (usage charges extra)', free: 'No', starter: 'Yes, with additional $49', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'Voice AI Agent ordering (usage/min charges extra)', free: 'No', starter: 'Yes, with additional $49', medium: 'Yes, with additional $99', custom: 'Yes' },
      ],
    },
    {
      section: 'Help / Support',
      rows: [
        { label: 'Email Support — 24 Hour SLA', free: 'Yes', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Phone Support — 12 Hour SLA', free: 'No', starter: 'Yes, with additional $49', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'Onboarding support — over phone', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
      ],
    },
  ],
  [FEATURE_KEYS.MARKETING_AUTOMATION]: [
    {
      section: null,
      rows: [
        { label: 'SEO + AEO services', free: 'No', starter: 'Yes, with additional $49/month', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'Email Marketing', free: 'No', starter: 'Yes — 1,000 emails/month', medium: 'Yes — 10k emails/month', custom: 'Unlimited' },
        { label: 'SMS Marketing', free: 'No', starter: 'Yes — 1,000 SMS/month', medium: 'Yes — 10k SMS/month', custom: 'Unlimited' },
        { label: 'WhatsApp Marketing', free: 'No', starter: 'Yes — 1,000 messages/month', medium: 'Yes — 10k messages/month', custom: 'Unlimited' },
      ],
    },
    {
      section: 'Social Media Posts',
      rows: [
        { label: 'Facebook', free: 'No', starter: '2 posts/week (addn $49)', medium: '4 posts/week (addn $99)', custom: 'Unlimited' },
        { label: 'Instagram', free: 'No', starter: '2 posts/week', medium: '4 posts/week', custom: 'Unlimited' },
        { label: 'TikTok', free: 'No', starter: '2 posts/week', medium: '4 posts/week', custom: 'Unlimited' },
        { label: 'Google — YouTube', free: 'No', starter: '2 posts/week', medium: '4 posts/week', custom: 'Unlimited' },
        { label: 'Manage Social Media Calendar', free: 'No', starter: 'Yes, with additional $49', medium: 'Yes, with additional $99', custom: 'Yes' },
      ],
    },
    {
      section: 'Paid Ads',
      rows: [
        { label: 'Meta', free: 'No', starter: 'Yes, with additional $49 + platform charges', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'Google', free: 'No', starter: 'Yes, with additional $49 + platform charges', medium: 'Yes, with additional $99', custom: 'Yes' },
        { label: 'TikTok', free: 'No', starter: 'Yes, with additional $49 + platform charges', medium: 'Yes, with additional $99', custom: 'Yes' },
      ],
    },
    {
      section: 'Marketing Automations',
      rows: [
        { label: 'New customer', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Onboard to Loyalty', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Lapsed customer', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Auto upsell', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
        { label: 'Auto apply coupons', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Yes' },
      ],
    },
  ],
  [FEATURE_KEYS.LOYALTY]: [
    {
      section: 'Loyalty Points',
      rows: [
        { label: 'Cashback Points with Tiers', free: 'No', starter: 'Yes, with additional $99/month', medium: 'Yes, with additional $129', custom: 'Yes' },
        { label: 'Rewards Program', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Unlimited' },
        { label: 'Referral Marketing', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Unlimited' },
        { label: 'Notifications/Reminders', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Unlimited' },
      ],
    },
    {
      section: 'Coupons',
      rows: [
        { label: 'Multi-level', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Unlimited' },
        { label: 'Private and Public', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Unlimited' },
        { label: 'BOGO/Bundles', free: 'No', starter: 'Yes', medium: 'Yes', custom: 'Unlimited' },
      ],
    },
  ],
};
